import { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import store from './utils/store';
import remoteStore from './utils/remoteStore';
import { downloadFinanceBackup, parseFinanceBackup } from './utils/excelBackup';
import AuthPage from './components/AuthPage';
import auth from './utils/auth';
import NavigationMenu from './components/NavigationMenu';
import DialogManager from './components/DialogManager';
import HomePage from './pages/HomePage';
import StatsPage from './pages/StatsPage';
import NotesPage from './pages/NotesPage';
import SettingsPage from './pages/SettingsPage';
import AccountPage from './pages/AccountPage';

// Утилита: проверяет, принадлежит ли дата тому же месяцу и году, что и reference (по умолчанию - текущая дата)
const isSameMonth = (dateStr, refDate = new Date()) => {
    const d = new Date(dateStr);
    return d.getFullYear() === refDate.getFullYear() && d.getMonth() === refDate.getMonth();
};

// Создаем темную тему с синими акцентами
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#2dd4bf',
        },
        secondary: {
            main: '#f59e0b',
        },
        background: {
            default: '#090d13',
            paper: '#111827',
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#111827',
                    borderRadius: '8px',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
    },
});

export default function FinanceApp() {
    const [isAuthenticated, setIsAuthenticated] = useState(auth.isAuthenticated());
    const [isInitializing, setIsInitializing] = useState(true);
    const [isDataLoaded, setIsDataLoaded] = useState(!remoteStore.isEnabled());
    const [activeTab, setActiveTab] = useState("home");
    const [categories, setCategories] = useState(() => store.getCategories());
    const [transactions, setTransactions] = useState(() => {
        const storedTransactions = store.getTransactions();
        return {
            expenses: storedTransactions?.expenses || [],
            income: storedTransactions?.income || [],
            savings: storedTransactions?.savings || []
        };
    });
    const [notes, setNotes] = useState(() => store.getNotes());
    const [dialogs, setDialogs] = useState({
        category: false,
        expense: false,
        income: false,
        goal: false,
        goalContribution: false, // Добавляем новый диалог
        note: false
    });
    const [currentForm, setCurrentForm] = useState({
        categoryType: 'expenses',
        category: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        limit: '',
        note: '',
        reminder: null,
        isTop: false
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // Добавляем в начало файла к остальным useState

    useEffect(() => {
        let isMounted = true;

        auth.init()
            .then(() => {
                if (isMounted) {
                    setIsAuthenticated(auth.isAuthenticated());
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsInitializing(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    // Обновляем эффект для корректной работы с данными при смене пользователя
    useEffect(() => {
        if (!isAuthenticated || isInitializing) return;

        if (remoteStore.isEnabled()) {
            let isMounted = true;
            setIsDataLoaded(false);

            remoteStore.loadAll()
                .then((data) => {
                    if (!isMounted) return;
                    setCategories(data.categories);
                    setTransactions(data.transactions);
                    setNotes(data.notes);
                    setIsDataLoaded(true);
                })
                .catch((error) => {
                    console.error('Error loading remote data:', error);
                    if (!isMounted) return;
                    setCategories(store.getCategories());
                    setTransactions(store.getTransactions());
                    setNotes(store.getNotes());
                    setIsDataLoaded(true);
                });

            return () => {
                isMounted = false;
            };
        } else {
            setCategories(store.getCategories());
            setTransactions(store.getTransactions());
            setNotes(store.getNotes());
            setIsDataLoaded(true);
        }
    }, [isAuthenticated, isInitializing]);

    // Сохраняем изменения в store
    useEffect(() => {
        if (isAuthenticated && isDataLoaded) {
            store.setCategories(categories);
            remoteStore.saveCategories(categories).catch((error) => {
                console.error('Error saving remote categories:', error);
            });
        }
    }, [categories, isAuthenticated, isDataLoaded]);

    useEffect(() => {
        if (isAuthenticated && isDataLoaded) {
            store.setTransactions(transactions);
            remoteStore.saveTransactions(transactions).catch((error) => {
                console.error('Error saving remote transactions:', error);
            });
        }
    }, [transactions, isAuthenticated, isDataLoaded]);

    useEffect(() => {
        if (isAuthenticated && isDataLoaded) {
            store.setNotes(notes);
            remoteStore.saveNotes(notes).catch((error) => {
                console.error('Error saving remote notes:', error);
            });
        }
    }, [notes, isAuthenticated, isDataLoaded]);

    if (isInitializing) {
        return (
            <ThemeProvider theme={darkTheme}>
            <div className="min-h-screen flex items-center justify-center bg-dark-900 text-white">
                    <div className="text-center">
                        <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                        <p className="text-sm text-gray-400">Загружаем финансы</p>
                    </div>
                </div>
            </ThemeProvider>
        );
    }

    // Рендеринг страницы авторизации если пользователь не авторизован
    if (!isAuthenticated) {
        return <AuthPage onAuthSuccess={() => setIsAuthenticated(true)} />;
    }

    if (!isDataLoaded) {
        return (
            <ThemeProvider theme={darkTheme}>
            <div className="min-h-screen flex items-center justify-center bg-dark-900 text-white">
                    <div className="text-center">
                        <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-cyan-300 border-t-transparent animate-spin" />
                        <p className="text-sm text-gray-400">Синхронизируем данные</p>
                    </div>
                </div>
            </ThemeProvider>
        );
    }

    // Подготовка данных для графиков
    const prepareChartData = () => {
        // Данные для круговой диаграммы расходов по категориям (только текущий месяц)
        const pieData = categories.expenses
            .map(category => {
                const spent = transactions.expenses
                    .filter(t => isSameMonth(t.date) && String(t.categoryId) === String(category.id))
                    .reduce((sum, t) => sum + t.amount, 0);
                return {
                    name: category.name,
                    value: spent
                };
            })
            .filter(item => item.value > 0); // Фильтруем категории с нулевыми расходами

        // Данные для графика расходов по времени (последние 7 дней с учетом выбранной недели)
        const today = new Date();
        today.setDate(today.getDate() + (selectedWeekOffset * 7)); // Смещаем дату на выбранное количество недель

        const last7Days = Array.from({length: 7}, (_, i) => {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0];
        }).reverse();

        const lineData = last7Days.map(date => {
            const dayExpenses = transactions.expenses
                .filter(t => t.date === date)
                .reduce((sum, t) => sum + t.amount, 0);
            const dayIncome = transactions.income
                .filter(t => t.date === date)
                .reduce((sum, t) => sum + t.amount, 0);
            return {
                date: new Date(date).toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit'}),
                expenses: dayExpenses,
                income: dayIncome
            };
        });

        return { pieData, lineData };
    };

    // Функции для работы с категориями
    const addCategory = (type) => {
        const name = currentForm.category.trim();
        const limit = Number(currentForm.limit);
        if (!name || ((type === 'expenses' || type === 'goals') && (!Number.isFinite(limit) || limit <= 0))) {
            return;
        }

        const newCategory = {
            id: Date.now(),
            name,
            ...(type === 'expenses' && { limit }),
            ...(type === 'goals' && { 
                target: limit,
                current: 0
            })
        };
        setCategories(prev => ({
            ...prev,
            [type]: [...prev[type], newCategory]
        }));
        handleCloseDialog('category');
    };

    // Добавляем функцию удаления категории
    const deleteCategory = (type, categoryId) => {
        if (!window.confirm('Вы уверены? Это также удалит все транзакции этой категории.')) {
            return;
        }

        // Удаляем категорию
        setCategories(prev => ({
            ...prev,
            [type]: prev[type].filter(cat => cat.id !== categoryId)
        }));

        // Удаляем связанные транзакции
        setTransactions(prev => ({
            ...prev,
            [type]: prev[type].filter(t => t.categoryId !== categoryId)
        }));
    };

    // Функции для работы с транзакциями
    const addTransaction = (type) => {
        const amount = Number(currentForm.amount);
        if (!currentForm.category || !currentForm.date || !Number.isFinite(amount) || amount <= 0) {
            return;
        }

        const newTransaction = {
            id: Date.now(),
            categoryId: currentForm.category,
            amount,
            date: currentForm.date
        };
        setTransactions(prev => ({
            ...prev,
            [type]: [...prev[type], newTransaction]
        }));
        handleCloseDialog(type);
    };

    // Добавляем новую функцию для удаления транзакции
    const deleteTransaction = (type, transactionId) => {
        setTransactions(prev => ({
            ...prev,
            [type]: prev[type].filter(t => t.id !== transactionId)
        }));
    };

    // Функции для работы с заметками
    const addNote = () => {
        const noteText = currentForm.note.trim();
        if (!noteText || !currentForm.date) {
            return;
        }

        // Если новая заметка помечена как топ, убираем топ-статус у всех остальных
        if (currentForm.isTop) {
            setNotes(prev => prev.map(note => ({ ...note, isTop: false })));
        }

        const newNote = {
            id: Date.now(),
            text: noteText,
            date: currentForm.date,
            reminder: currentForm.reminder,
            isTop: currentForm.isTop
        };
        setNotes(prev => [...prev, newNote]);
        handleCloseDialog('note');
    };

    const toggleTopNote = (noteId) => {
        setNotes(prev => prev.map(note => ({
            ...note,
            isTop: note.id === noteId ? !note.isTop : false
        })));  // There was a missing closing parenthesis here
    };

    const deleteNote = (noteId) => {
        if (!window.confirm('Удалить заметку?')) return;
        setNotes(prev => prev.filter(note => note.id !== noteId));
    };

    const handleCloseDialog = (type) => {
        setDialogs(prev => ({ ...prev, [type]: false }));
        if (type === 'goal' || type === 'goalContribution') {
            setSelectedGoal(null);
        }
        setCurrentForm({
            categoryType: 'expenses',
            category: '',
            amount: '',
            date: new Date().toISOString().split('T')[0],
            limit: '',
            note: '',
            reminder: null,
            isTop: false
        });
    };

    // Вычисление статистики
    const calculateStats = () => {
        const now = new Date();
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const daysPassed = now.getDate();
        const daysLeft = daysInMonth - daysPassed;

        // Считаем расходы только за текущий месяц
        const totalExpenses = transactions.expenses
            .filter(t => isSameMonth(t.date))
            .reduce((sum, t) => sum + t.amount, 0);

        const dailyAverage = daysPassed > 0 ? (totalExpenses / daysPassed) : 0;
        const projectedExpenses = Math.round(dailyAverage * daysInMonth);

        const topNote = notes.find(note => note.isTop);

        return {
            daysPassed,
            daysLeft,
            totalExpenses,
            dailyAverage,
            projectedExpenses,
            topNote
        };
    };
    // Функции для работы с Excel
    const downloadExcel = () => {
        downloadFinanceBackup({ categories, transactions, notes });
    };

    const uploadExcel = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const importedData = await parseFinanceBackup(file);
            setCategories(importedData.categories);
            setTransactions(importedData.transactions);
            setNotes(importedData.notes);
        } catch (error) {
            console.error('Error importing finance backup:', error);
            alert('Не удалось импортировать Excel. Проверьте, что файл создан экспортом Financer и не поврежден.');
        } finally {
            event.target.value = '';
        }
    };
    const handleClearAll = async () => {
        if (window.confirm('Вы уверены? Это действие удалит все данные безвозвратно!')) {
            await remoteStore.clearAll().catch((error) => {
                console.error('Error clearing remote data:', error);
            });
            store.clearAll();
            setCategories({ expenses: [], income: [], goals: [] });
            setTransactions({ expenses: [], income: [], savings: [] });
            setNotes([]);
        }
    };

    // Обновляем функцию форматирования даты
    const formatDate = (year, month, day) => {
        // Устанавливаем дату в полночь по локальному времени
        const date = new Date(year, month, day, 12); // используем 12 часов дня чтобы избежать проблем с часовыми поясами
        return date.toISOString().split('T')[0];
    };

    // Обновляем функцию getDayTransactions
    const getDayTransactions = (dateStr) => {
        const searchDate = dateStr;
        
        const dayTransactions = {
            expenses: transactions.expenses.filter(t => t.date === searchDate),
            income: transactions.income.filter(t => t.date === searchDate)
        };

        return {
            expenses: dayTransactions.expenses.map(t => ({
                ...t,
                categoryName: categories.expenses.find(c => String(c.id) === String(t.categoryId))?.name || 'Удаленная категория'
            })),
            income: dayTransactions.income.map(t => ({
                ...t,
                categoryName: categories.income.find(c => String(c.id) === String(t.categoryId))?.name || 'Удаленная категория'
            })),
            totalExpenses: dayTransactions.expenses.reduce((sum, t) => sum + t.amount, 0),
            totalIncome: dayTransactions.income.reduce((sum, t) => sum + t.amount, 0)
        };
    };

    const contributeToGoal = (goalId = currentForm.category) => {
        const amount = Number(currentForm.amount);
        const targetGoalId = selectedGoal?.id ?? goalId;

        if (!targetGoalId || !Number.isFinite(amount) || amount <= 0) {
            return;
        }

        setCategories(prev => ({
            ...prev,
            goals: prev.goals.map(goal => 
                String(goal.id) === String(targetGoalId)
                ? { ...goal, current: Math.min(goal.current + amount, goal.target) }
                : goal
            )
        }));
        handleCloseDialog(selectedGoal ? 'goalContribution' : 'goal');
    };

    const handleFormChange = (changes) => {
        setCurrentForm(prev => ({ ...prev, ...changes }));
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <div className="app-surface">
                <NavigationMenu 
                    activeTab={activeTab} 
                    onTabChange={setActiveTab} 
                />

                <main className="md:ml-64 p-4 md:p-8 min-h-screen pb-24 md:pb-8">
                    {activeTab === "home" && (
                        <HomePage
                            transactions={transactions}
                            categories={categories}
                            setDialogs={setDialogs}
                        />
                    )}

                    {activeTab === "stats" && (
                        <StatsPage
                            prepareChartData={prepareChartData}
                            calculateStats={calculateStats}
                            formatDate={formatDate}
                            getDayTransactions={getDayTransactions}
                            categories={categories}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            setSelectedGoal={setSelectedGoal}
                            setDialogs={setDialogs}
                            transactions={transactions}
                            deleteTransaction={deleteTransaction}
                            selectedWeekOffset={selectedWeekOffset}
                            setSelectedWeekOffset={setSelectedWeekOffset}
                        />
                    )}

                    {activeTab === "settings" && (
                        <SettingsPage 
                            categories={categories}
                            deleteCategory={deleteCategory}
                            setDialogs={setDialogs}
                        />
                    )}

                    {activeTab === "notes" && (
                        <NotesPage
                            notes={notes}
                            toggleTopNote={toggleTopNote}
                            deleteNote={deleteNote}
                            setDialogs={setDialogs}
                        />
                    )}

                    {activeTab === "account" && (
                        <AccountPage
                            downloadExcel={downloadExcel}
                            uploadExcel={uploadExcel}
                            handleClearAll={handleClearAll}
                            onLogout={async () => {
                                await auth.logout();
                                setIsAuthenticated(false);
                                setIsDataLoaded(!remoteStore.isEnabled());
                            }}
                        />
                    )}
                </main>

                <DialogManager
                    dialogs={dialogs}
                    currentForm={currentForm}
                    selectedGoal={selectedGoal}
                    categories={categories}
                    selectedDate={selectedDate}
                    onSetSelectedDate={setSelectedDate}
                    getDayTransactions={getDayTransactions}
                    onClose={handleCloseDialog}
                    onFormChange={handleFormChange}
                    onAddCategory={addCategory}
                    onAddTransaction={addTransaction}
                    onAddNote={addNote}
                    onContributeToGoal={contributeToGoal}
                    deleteTransaction={deleteTransaction}
                />
            </div>
        </ThemeProvider>
    );
}
